import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Package, RefreshCw, Search, Truck } from 'lucide-react';
import { readCkScope, scopeKey, useCkScope } from '../components/CkOperationsShared';
import { createReceivingApi } from './api';
import Arrival from './Arrival';
import './receiving.css';

export default function InboundQueue({ userId }) {
  const scope = useCkScope();
  return <QueueWorkspace key={`${userId}:${scopeKey(scope)}`} userId={userId} scope={scope} />;
}

function QueueWorkspace({ userId, scope }) {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [canOperate, setCanOperate] = useState(false);
  const alive = useRef(false);
  const pending = useRef(null);
  const api = useMemo(() => createReceivingApi(() => ({ ...readCkScope(), userId })), [userId]);
  const active = useCallback(() => alive.current && scopeKey(readCkScope()) === scopeKey(scope), [scope]);
  const load = useCallback(async () => {
    if (!active() || pending.current) return;
    const controller = new AbortController();
    pending.current = controller;
    setLoading(true); setError(''); setOrders([]); setCanOperate(false);
    try {
      const capabilities = await api.capabilities({ signal: controller.signal });
      const data = await api.queue({ signal: controller.signal });
      if (active() && !controller.signal.aborted) { setOrders(data); setCanOperate(capabilities.can_operate === true); }
    } catch (e) {
      if (active() && !controller.signal.aborted) setError(e.message);
    } finally {
      if (pending.current === controller) pending.current = null;
      if (active() && !controller.signal.aborted) setLoading(false);
    }
  }, [api, active]);
  useEffect(() => {
    alive.current = true;
    load();
    return () => { alive.current = false; pending.current?.abort(); pending.current = null; };
  }, [load]);

  const query = search.trim().toLowerCase();
  const visible = orders.filter(po => [po.id, po.po_number, po.supplier_name, po.dock_id, po.expected_date]
    .some(value => String(value || '').toLowerCase().includes(query)));
  return <main className="receiving-queue" aria-label="Inbound receiving">
    <header className="receiving-heading"><div><p className="receiving-eyebrow">Receiving</p><h1><Truck aria-hidden="true" size={26} />Inbound deliveries</h1></div>
      <button type="button" title="Refresh deliveries" aria-label="Refresh deliveries" disabled={loading} onClick={load}><RefreshCw size={20} /></button>
    </header>
    <nav className="receiving-links" aria-label="Receiving navigation"><a href="/m/inbox">My receipts</a></nav>
    <div className="receiving-toolbar"><label><Search size={18} aria-hidden="true" /><input type="search" aria-label="Search deliveries" placeholder="Supplier, PO or dock" value={search} onChange={e => setSearch(e.target.value)} /></label>
      {!loading && !error && <span role="status">{visible.length} of {orders.length} deliveries</span>}
    </div>
    {loading && <p role="status">Loading deliveries...</p>}
    {error && <p role="alert" className="receiving-error">{error}</p>}
    {!loading && !error && <>
      {orders.length === 200 && <p role="status">Showing the first 200 pending deliveries.</p>}
      {!visible.length && <p className="receiving-empty"><Package size={24} aria-hidden="true" />{query ? 'No matching deliveries.' : 'No pending deliveries at this site.'}</p>}
      <div className="receiving-orders">{orders.map(po => <details key={po.id} hidden={!visible.includes(po)} className="receiving-order">
        <summary><div className="receiving-order-title"><strong>{po.supplier_name || 'Supplier not specified'}</strong><span className="receiving-status">{po.status === 'partially_received' ? 'Part received' : 'Awaiting delivery'}</span></div>
          <span className="receiving-po">{po.po_number || `PO ${po.id}`}</span>
          <span className="receiving-meta"><span>Expected: {po.expected_date || 'Not scheduled'}</span><span>Dock: {po.dock_id || 'Not assigned'}</span><span>{po.lines?.length || 0} {po.lines?.length === 1 ? 'line' : 'lines'}</span></span>
        </summary>
        <div className="receiving-manifest"><h2>Order manifest</h2>
          {po.expected_window_from && <p>Window: {po.expected_window_from} to {po.expected_window_to}</p>}
          <ul>{(po.lines || []).map((line, index) => <li key={index}><strong>{line.description || line.ingredient_id || `Line ${index + 1}`}</strong><span>Ordered: {line.qty} {line.unit}</span><span>Received: {line.received_qty ?? '0'} {line.unit}</span></li>)}</ul>
        </div>
        <Arrival po={po} api={api} active={active} canOperate={canOperate} />
      </details>)}</div>
    </>}
  </main>;
}
